import {
   Box,
   TextField,
   DialogContent,
   IconButton,
   DialogTitle,
   Alert,
   Grid,
   MenuItem,
   Select,
   InputLabel,
   FormControl,
   Button,
   CircularProgress,
   Typography,
   Dialog,
   DialogActions,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider, DateTimePickerProps } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useState } from 'react';
import GooglePlacesSearch from '../components/common/GooglePlacesSearch';
import EventIcon from '@mui/icons-material/Event';
import FlightIcon from '@mui/icons-material/Flight';
import CloseIcon from '@mui/icons-material/Close';
import { db } from '../firebase/config';
import { collection, doc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { addDoc } from 'firebase/firestore';
import { useUser } from '../contexts/UserContext';

export const MapViewTripPopup = ({
   open,
   onClose,
   addTripToView,
}: {
   open: boolean;
   onClose: () => void;
   addTripToView: (trip: any) => void;
}) => {
   const [tripSaveError, setTripSaveError] = useState('');
   const { currentUser } = useUser();

   const [newTrip, setNewTrip] = useState({
      title: '',
      startDate: new Date(),
      endDate: new Date(new Date().setDate(new Date().getDate() + 7)),
      primaryDestination: {
         address: '',
         name: '',
         coordinates: [0, 0],
      },
      description: '',
   });

   const [savingTrip, setSavingTrip] = useState(false);

   const createTrip = async () => {
      if (!currentUser) return;
      if (!newTrip.title.trim()) {
         setTripSaveError('Trip title is required');
         return;
      }

      setSavingTrip(true);
      setTripSaveError('');

      try {
         // Create the trip document in Firestore
         const tripData = {
            title: newTrip.title,
            description: newTrip.description,
            primaryDestination: {
               address: newTrip.primaryDestination?.address,
               name: newTrip.primaryDestination?.name,
               coordinates: newTrip.primaryDestination?.coordinates,
            },
            startDate: Timestamp.fromDate(newTrip.startDate),
            endDate: Timestamp.fromDate(newTrip.endDate),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            owner: currentUser.uid,
            isHistorical: true,
         };

         // Use backend API to create trip with proper members array
         const { createApiUrl } = await import('../utils/apiConfig');
         const { getAuthHeaders } = await import('../utils/auth');
         const headers = await getAuthHeaders();
         const apiUrl = createApiUrl('/api/trips');

         const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify({
               title: newTrip.title,
               description: newTrip.description,
               startDate: newTrip.startDate.toISOString(),
               endDate: newTrip.endDate.toISOString(),
               isPublic: false,
               primaryDestination: newTrip.primaryDestination,
               userId: currentUser.uid,
            }),
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.error || 'Failed to create trip');
         }

         const docRef = { id: result.tripId };
         if (addTripToView) {
            const trip = {
               ...tripData,
               id: docRef.id,
               isShared: false,
               isOwner: true,
            };
            addTripToView(trip);
         }

         if (onClose) {
            onClose();
         }
      } catch (error) {
         console.error('Error creating trip:', error);
         setTripSaveError('Failed to create trip. Please try again.');
      } finally {
         setSavingTrip(false);
      }
   };

   return (
      <>
         {/* Add Trip Dialog */}
         <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6'>
                     <FlightIcon color='primary' sx={{ mr: 1, verticalAlign: 'middle' }} />
                     Add New Trip
                  </Typography>
                  <IconButton onClick={onClose} size='small'>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent dividers>
               {tripSaveError && (
                  <Alert severity='error' sx={{ mb: 2 }}>
                     {tripSaveError}
                  </Alert>
               )}
               <Grid container spacing={2}>
                  <Grid item xs={12}>
                     <TextField
                        label='Trip Title'
                        fullWidth
                        required
                        value={newTrip.title}
                        onChange={(e) => setNewTrip((prev) => ({ ...prev, title: e.target.value }))}
                        disabled={savingTrip}
                        placeholder='e.g., Summer Vacation'
                     />
                  </Grid>

                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                     <Grid item xs={12} sm={6}>
                        <Box sx={{ width: '100%' }}>
                           <DateTimePicker<Date>
                              label='Start Date'
                              value={newTrip.startDate}
                              onChange={(newValue: Date | null) => {
                                 if (newValue) {
                                    setNewTrip((prev) => {
                                       // If the start date is after the end date, update the end date too
                                       const updatedEndDate =
                                          newValue > prev.endDate
                                             ? new Date(
                                                  newValue.getTime() + 7 * 24 * 60 * 60 * 1000
                                               )
                                             : prev.endDate;
                                       return {
                                          ...prev,
                                          startDate: newValue,
                                          endDate: updatedEndDate,
                                       };
                                    });
                                 }
                              }}
                              disabled={savingTrip}
                              renderInput={(props) => <TextField {...props} fullWidth />}
                           />
                        </Box>
                     </Grid>
                     <Grid item xs={12} sm={6}>
                        <Box sx={{ width: '100%' }}>
                           <DateTimePicker<Date>
                              label='End Date'
                              value={newTrip.endDate}
                              onChange={(newValue: Date | null) => {
                                 if (newValue) {
                                    setNewTrip((prev) => ({ ...prev, endDate: newValue }));
                                 }
                              }}
                              disabled={savingTrip}
                              minDate={newTrip.startDate}
                              renderInput={(props) => <TextField {...props} fullWidth />}
                           />
                        </Box>
                     </Grid>
                  </LocalizationProvider>

                  <Grid item xs={12}>
                     {/* <TextField
                        label='Destination'
                        fullWidth
                        value={newTrip.primaryDestination?.name || ''}
                        onChange={(e) =>
                           setNewTrip((prev) => ({
                              ...prev,
                              primaryDestination: {
                                 ...prev.primaryDestination,
                                 name: e.target.value,
                                 coordinates: prev.primaryDestination?.coordinates || [0, 0],
                              } as any,
                           }))
                        }
                        disabled={savingTrip}
                        placeholder='e.g., Paris, France'
                     /> */}
                     <GooglePlacesSearch
                        //  sx={{ mb: 2 }}
                        value={newTrip.primaryDestination?.name || ''}
                        searchType='places'
                        label='Destination'
                        fullWidth
                        onPlaceSelect={(place_name: any, lat: any, lng: any) => {
                           setNewTrip((prev) => ({
                              ...prev,
                              primaryDestination: {
                                 address: place_name,
                                 name: place_name,
                                 coordinates: [lat, lng],
                              },
                           }));
                        }}
                     />
                  </Grid>

                  <Grid item xs={12}>
                     <TextField
                        label='Description'
                        fullWidth
                        multiline
                        rows={3}
                        value={newTrip.description || ''}
                        onChange={(e) =>
                           setNewTrip((prev) => ({ ...prev, description: e.target.value }))
                        }
                        disabled={savingTrip}
                        placeholder='Add details about this trip...'
                     />
                  </Grid>
               </Grid>
            </DialogContent>
            <DialogActions>
               <Button onClick={onClose} disabled={savingTrip}>
                  Cancel
               </Button>
               <Button
                  variant='contained'
                  color='primary'
                  onClick={createTrip}
                  disabled={savingTrip}
                  startIcon={savingTrip ? <CircularProgress size={20} /> : <FlightIcon />}
               >
                  {savingTrip ? 'Saving...' : 'Create Trip'}
               </Button>
            </DialogActions>
         </Dialog>
      </>
   );
};

export const MapViewEventPopup = ({
   open,
   onClose,
   addEventToView,
}: {
   open: boolean;
   onClose: () => void;
   addEventToView: (event: any) => void;
}) => {
   const { currentUser } = useUser();
   const [eventSaveError, setEventSaveError] = useState('');
   const [newEvent, setNewEvent] = useState({
      title: '',
      eventType: 'personal',
      startDate: new Date(),
      endDate: new Date(new Date().setHours(new Date().getHours() + 2)),
      location: {
         address: '',
         name: '',
         coordinates: [0, 0],
      },
      description: '',
   });

   const [savingEvent, setSavingEvent] = useState(false);

   const createEvent = async () => {
      if (!currentUser) return;
      if (!newEvent.title.trim()) {
         setEventSaveError('Event title is required');
         return;
      }

      setSavingEvent(true);
      setEventSaveError('');

      try {
         // Use backend API to create event with proper members array
         const { createApiUrl } = await import('../utils/apiConfig');
         const apiUrl = createApiUrl('/api/events');

         const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               title: newEvent.title.trim(),
               description: newEvent.description?.trim(),
               startDate: newEvent.startDate.toISOString(),
               endDate: newEvent.endDate.toISOString(),
               location: newEvent.location,
               eventType: newEvent.eventType,
               isPublic: false,
               userId: currentUser.uid,
            }),
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.error || 'Failed to create event');
         }

         if (addEventToView) {
            const event = {
               id: result.eventId,
               title: newEvent.title.trim(),
               startDate: newEvent.startDate,
               endDate: newEvent.endDate,
               location: newEvent.location,
               description: newEvent.description?.trim(),
               eventType: newEvent.eventType,
               isPublic: false,
               userId: currentUser.uid,
               isShared: false,
               isOwner: true,
            };
            addEventToView(event);
         }
         if (onClose) {
            onClose();
         }
      } catch (error) {
         console.error('Error creating event:', error);
         setEventSaveError('Failed to create event. Please try again.');
      } finally {
         setSavingEvent(false);
      }
   };

   return (
      <>
         <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6'>
                     <EventIcon color='secondary' sx={{ mr: 1, verticalAlign: 'middle' }} />
                     Add New Event
                  </Typography>
                  <IconButton onClick={onClose} size='small'>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent dividers>
               {eventSaveError && (
                  <Alert severity='error' sx={{ mb: 2 }}>
                     {eventSaveError}
                  </Alert>
               )}
               <Grid container spacing={2}>
                  <Grid item xs={12}>
                     <TextField
                        label='Event Title'
                        fullWidth
                        required
                        value={newEvent.title}
                        onChange={(e) =>
                           setNewEvent((prev) => ({ ...prev, title: e.target.value }))
                        }
                        disabled={savingEvent}
                     />
                  </Grid>

                  <Grid item xs={12}>
                     <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id='event-type-label'>Event Type</InputLabel>
                        <Select
                           labelId='event-type-label'
                           value={newEvent.eventType}
                           label='Event Type'
                           onChange={(e) =>
                              setNewEvent((prev) => ({ ...prev, eventType: e.target.value }))
                           }
                           disabled={savingEvent}
                        >
                           <MenuItem value='personal'>Personal</MenuItem>
                           <MenuItem value='business'>Business</MenuItem>
                           <MenuItem value='vacation'>Vacation Activity</MenuItem>
                           <MenuItem value='reminder'>Reminder</MenuItem>
                           <MenuItem value='social'>Social</MenuItem>
                           <MenuItem value='other'>Other</MenuItem>
                        </Select>
                     </FormControl>
                  </Grid>

                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                     <Grid item xs={12} sm={6}>
                        <Box sx={{ width: '100%' }}>
                           <DateTimePicker<Date>
                              label='Start Date & Time'
                              value={newEvent.startDate}
                              onChange={(newValue: Date | null) => {
                                 if (newValue) {
                                    setNewEvent((prev) => {
                                       // If the start date is after the end date, update the end date too
                                       const updatedEndDate =
                                          newValue > prev.endDate
                                             ? new Date(newValue.getTime() + 2 * 60 * 60 * 1000)
                                             : prev.endDate;
                                       return {
                                          ...prev,
                                          startDate: newValue,
                                          endDate: updatedEndDate,
                                       };
                                    });
                                 }
                              }}
                              disabled={savingEvent}
                              renderInput={(props) => <TextField {...props} fullWidth />}
                           />
                        </Box>
                     </Grid>
                     <Grid item xs={12} sm={6}>
                        <Box sx={{ width: '100%' }}>
                           <DateTimePicker<Date>
                              label='End Date & Time'
                              value={newEvent.endDate}
                              onChange={(newValue: Date | null) => {
                                 if (newValue) {
                                    setNewEvent((prev) => ({ ...prev, endDate: newValue }));
                                 }
                              }}
                              disabled={savingEvent}
                              minDate={newEvent.startDate}
                              renderInput={(props) => <TextField {...props} fullWidth />}
                           />
                        </Box>
                     </Grid>
                  </LocalizationProvider>
                  {/* 
                  <Grid item xs={12}>
                     <TextField
                        label='Location Name'
                        fullWidth
                        value={newEvent.location?.name || ''}
                        onChange={(e) =>
                           setNewEvent((prev) => ({
                              ...prev,
                              location: { ...prev.location, name: e.target.value } as any,
                           }))
                        }
                        disabled={savingEvent}
                        placeholder='e.g., Central Park'
                     />
                  </Grid> */}

                  <Grid item xs={12}>
                     {/* <TextField
                        label='Address'
                        fullWidth
                        value={newEvent.location?.address || ''}
                        onChange={(e) =>
                           setNewEvent((prev) => ({
                              ...prev,
                              location: { ...prev.location, address: e.target.value } as any,
                           }))
                        }
                        disabled={savingEvent}
                        placeholder='e.g., New York, NY'
                     /> */}
                     <GooglePlacesSearch
                        //  sx={{ mb: 2 }}
                        value={newEvent.location?.address || ''}
                        searchType='places'
                        label='Location'
                        fullWidth
                        onPlaceSelect={(place_name: any, lat: any, lng: any) => {
                           setNewEvent((prev) => ({
                              ...prev,
                              location: {
                                 address: place_name,
                                 name: place_name,
                                 coordinates: [lat, lng],
                              },
                           }));
                        }}
                     />
                  </Grid>

                  <Grid item xs={12}>
                     <TextField
                        label='Description'
                        fullWidth
                        multiline
                        rows={3}
                        value={newEvent.description || ''}
                        onChange={(e) =>
                           setNewEvent((prev) => ({ ...prev, description: e.target.value }))
                        }
                        disabled={savingEvent}
                        placeholder='Add details about this event...'
                     />
                  </Grid>
               </Grid>
            </DialogContent>
            <DialogActions>
               <Button onClick={onClose} disabled={savingEvent}>
                  Cancel
               </Button>
               <Button
                  variant='contained'
                  color='secondary'
                  onClick={createEvent}
                  disabled={savingEvent}
                  startIcon={savingEvent ? <CircularProgress size={20} /> : <EventIcon />}
               >
                  {savingEvent ? 'Saving...' : 'Create Event'}
               </Button>
            </DialogActions>
         </Dialog>
      </>
   );
};
